import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import firebase from 'firebase/compat/app';
import { TutorialState } from '../models/tutorial-state.model';

/**
 * Service for managing tutorial completion and dismissal state.
 * 
 * Tutorial state is persisted to Firestore under each user's board document:
 * `boards/{boardId}/users/{userEmail}/tutorialState`
 * 
 * Features:
 * - Real-time synchronization of tutorial state across devices
 * - Non-blocking fire-and-forget writes (don't await in UI flow)
 * - Backward compatible with users who don't have tutorialState field yet
 * - Atomic array updates using Firestore arrayUnion() to prevent race conditions
 */
@Injectable({
  providedIn: 'root'
})
export class TutorialStateService {
  /** Current tutorial state loaded from subscription */
  private currentState: TutorialState | null = null;
  
  /** Active subscription for real-time state updates */
  private stateSubscription: Subscription | null = null;
  
  /** Promise that resolves when initial state is loaded from Firestore */
  private stateInitialized: Promise<void> | null = null;

  constructor(private db: AngularFirestore) { }

  /**
    * Initialize real-time subscription to tutorial state.
    * Should be called when a user enters a board context.
    * 
    * Returns a Promise that resolves once the initial state is loaded from Firestore.
    * This prevents the race condition where tutorials are filtered before state is loaded.
    * 
    * @param boardId - The board ID
    * @param userId - The user's email (used as document ID in Firestore)
    * @returns Promise that resolves when initial state has been loaded from Firestore
    */
  init(boardId: string, userId: string): Promise<void> {
    // Clean up any existing subscription
    this.cleanup();
    
    // Create promise that resolves when first state emission is received
    this.stateInitialized = new Promise(resolve => {
      // Set up new subscription for real-time updates
      this.stateSubscription = this.getTutorialState(boardId, userId)
        .subscribe(state => {
          this.currentState = state;
          // Resolve promise on first emission to signal state is loaded
          resolve();
        });
    });
    
    return this.stateInitialized;
  }

  /**
   * Wait for the tutorial state to be initialized from Firestore.
   * 
   * Useful for operations that need to wait for state to be loaded before proceeding.
   * Returns a promise that resolves once the first state emission has been received.
   * 
   * @returns Promise that resolves when initial state has been loaded from Firestore
   */
  waitForInitialization(): Promise<void> {
    if (!this.stateInitialized) {
      // If init() hasn't been called yet, return a rejected promise
      return Promise.reject(new Error('Tutorial state has not been initialized. Call init() first.'));
    }
    return this.stateInitialized;
  }

  /**
   * Clean up subscription when leaving board context.
   * Should be called in component ngOnDestroy or when switching boards.
   */
  cleanup(): void {
    if (this.stateSubscription) {
      this.stateSubscription.unsubscribe();
      this.stateSubscription = null;
    }
    this.currentState = null;
    this.stateInitialized = null;
  }

  /**
   * Get real-time observable of tutorial state for a user.
   * 
   * Provides:
   * - Real-time updates if state changes on another device
   * - Backward compatibility: returns empty state for missing tutorialState field
   * 
   * @param boardId - The board ID
   * @param userId - The user's email (used as document ID in Firestore)
   * @returns Observable that emits the current TutorialState
   */
  getTutorialState(boardId: string, userId: string): Observable<TutorialState> {
    return this.db.collection('boards')
      .doc(boardId)
      .collection('users')
      .doc(userId)
      .valueChanges()
      .pipe(
        map((userData: any) => {
          // Handle backward compatibility: if tutorialState field doesn't exist, return empty state
          // Also ensure arrays exist even if tutorialState is partial
          const tutorialState = userData?.tutorialState;
          return {
            completedTutorials: tutorialState?.completedTutorials ?? [],
            dismissedTutorials: tutorialState?.dismissedTutorials ?? [],
            lastSeenVersion: tutorialState?.lastSeenVersion
          };
        })
      );
  }

  /**
   * Check if a tutorial has been marked as completed by the user.
   * Returns false if state hasn't been loaded yet.
   * 
   * @param tutorialId - The tutorial ID to check
   * @returns true if tutorial is in completedTutorials array, false otherwise
   */
  hasTutorialBeenCompleted(tutorialId: string): boolean {
    return this.currentState?.completedTutorials?.includes(tutorialId) ?? false;
  }

  /**
   * Check if a tutorial has been dismissed by the user.
   * Returns false if state hasn't been loaded yet.
   * 
   * @param tutorialId - The tutorial ID to check
   * @returns true if tutorial is in dismissedTutorials array, false otherwise
   */
  hasTutorialBeenDismissed(tutorialId: string): boolean {
    return this.currentState?.dismissedTutorials?.includes(tutorialId) ?? false;
  }

  /**
   * Mark a tutorial as completed.
   * 
   * This is a fire-and-forget operation - don't await in UI flow.
   * Uses atomic arrayUnion() to safely add to the array without race conditions.
   * Handles missing tutorialState field by using set() with merge option.
   * 
   * @param boardId - The board ID
   * @param userId - The user's email (used as document ID in Firestore)
   * @param tutorialId - The tutorial ID to mark as completed
   * @returns Promise that resolves when write completes (typically not awaited)
   */
  async markTutorialCompleted(boardId: string, userId: string, tutorialId: string): Promise<void> {
    try {
      const docRef = this.db.collection('boards')
        .doc(boardId)
        .collection('users')
        .doc(userId);

      // Use set with merge to handle missing tutorialState field
      // arrayUnion ensures atomic update and prevents duplicates
      await docRef.set({
        tutorialState: {
          completedTutorials: firebase.firestore.FieldValue.arrayUnion(tutorialId)
        }
      }, { merge: true });
    } catch (error) {
      // Non-critical path: log error but don't throw
      // Tutorial state persistence should not break user experience
      console.error('Failed to mark tutorial completed:', error);
    }
  }

  /**
   * Mark a tutorial as dismissed by the user.
   * 
   * This is a fire-and-forget operation - don't await in UI flow.
   * Uses atomic arrayUnion() to safely add to the array without race conditions.
   * Handles missing tutorialState field by using set() with merge option.
   * 
   * @param boardId - The board ID
   * @param userId - The user's email (used as document ID in Firestore)
   * @param tutorialId - The tutorial ID to mark as dismissed
   * @returns Promise that resolves when write completes (typically not awaited)
   */
  async markTutorialDismissed(boardId: string, userId: string, tutorialId: string): Promise<void> {
    try {
      const docRef = this.db.collection('boards')
        .doc(boardId)
        .collection('users')
        .doc(userId);

      // Use set with merge to handle missing tutorialState field
      // arrayUnion ensures atomic update and prevents duplicates
      await docRef.set({
        tutorialState: {
          dismissedTutorials: firebase.firestore.FieldValue.arrayUnion(tutorialId)
        }
      }, { merge: true });
    } catch (error) {
      // Non-critical path: log error but don't throw
      // Tutorial state persistence should not break user experience
      console.error('Failed to mark tutorial dismissed:', error);
    }
  }
}
